import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {blocksApi} from '../api';
import {StoriesFilter, StoryWithMetaModel, CreateStoryParams} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type BlockStoriesState = {
  getInfiniteStories: UseInfiniteQueryResult<InfiniteData<StoryWithMetaModel>, Error>;
  filters: StoriesFilter;
  updateFilter: (newFilter: Partial<StoriesFilter>) => void;
  createStoryMutation: UseMutationResult<unknown, Error, CreateStoryParams>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useBlockStories = (initialFilter: StoriesFilter): BlockStoriesState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<StoriesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteStories = useInfiniteQuery<
    StoryWithMetaModel,
    Error,
    InfiniteData<StoryWithMetaModel>,
    ['block-stories', 'infinite', Omit<StoriesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['block-stories', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return blocksApi.getBlockStories({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<StoriesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createStoryMutation = useMutation({
    mutationFn: blocksApi.createStory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['block-stories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'История успешно создана',
        name: 'createStory',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteStories,
    filters,
    updateFilter,
    createStoryMutation,
  };
};
